import { useState, useCallback } from 'react';
import type { ListData } from '../../../../types';

export interface QueueController {
  // State
  nowPlaying: ListData | null;
  nowPlayingId: string | null;
  currentPlaylist: ListData | null;

  // Methods
  setNowPlaying: (content: ListData | null) => void;
  setCurrentPlaylist: (playlist: ListData | null) => void;

  // Queue operations (return the content to play, not playlist wrapper)
  fetchNext: () => Promise<ListData | null>;
  advanceInPlaylist: () => Promise<ListData | null>;
  retreatInPlaylist: () => Promise<ListData | null>;
  removeFromQueue: (id: string) => Promise<void>;
  addToQueue: (
    id: string,
    position: number,
    playlistPosition?: number
  ) => Promise<void>;

  // Utility
  resolvePlaylistContent: (
    playlist: ListData,
    position?: number
  ) => Promise<ListData | null>;
  fetchPreviousFromHistory: (
    excludeId: string | null
  ) => Promise<ListData | null>;
}

export function useQueueController(): QueueController {
  const [nowPlaying, setNowPlayingState] = useState<ListData | null>(null);
  const [currentPlaylist, setCurrentPlaylistState] = useState<ListData | null>(
    null
  );

  const nowPlayingId = nowPlaying?.id ?? null;

  const setNowPlaying = useCallback((content: ListData | null) => {
    setNowPlayingState(content);
  }, []);

  const setCurrentPlaylist = useCallback((playlist: ListData | null) => {
    setCurrentPlaylistState(playlist);
  }, []);

  // Resolve playlist to its current content item
  const resolvePlaylistContent = useCallback(
    async (playlist: ListData, position?: number): Promise<ListData | null> => {
      const contentIds: string[] = JSON.parse(playlist.contentIds || '[]');
      if (contentIds.length === 0) return null;

      const targetIndex = position ?? playlist.playlistPosition ?? 0;
      if (targetIndex >= contentIds.length) return null;

      const targetContentId = contentIds[targetIndex];
      const response = await fetch(`/api/content/${targetContentId}`, {
        credentials: 'include',
      });
      const content = await response.json();

      if (content.error) return null;
      return content;
    },
    []
  );

  // Add item to queue
  const addToQueue = useCallback(
    async (
      id: string,
      position: number,
      playlistPosition?: number
    ): Promise<void> => {
      const body: Record<string, unknown> = { contentId: id, position };
      if (playlistPosition !== undefined) {
        body.playlistPosition = playlistPosition;
      }

      await fetch('/api/queue/add', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    },
    []
  );

  // Remove item from queue
  const removeFromQueue = useCallback(async (id: string): Promise<void> => {
    await fetch('/api/queue/remove', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contentId: id }),
    });
  }, []);

  // Fetch next item from queue, resolving playlists to content
  const fetchNext = useCallback(async (): Promise<ListData | null> => {
    const response = await fetch('/api/queue', {
      credentials: 'include',
    });
    const queueData = await response.json();
    const queueItems = queueData.items || [];

    if (queueItems.length === 0) {
      setNowPlayingState(null);
      setCurrentPlaylistState(null);
      return null;
    }

    const next = queueItems[0];

    if (next.type === 'playlist') {
      const contentIds: string[] = JSON.parse(next.contentIds || '[]');

      // If playlist is exhausted, remove and recurse
      if (
        next.playlistPosition !== null &&
        next.playlistPosition !== undefined &&
        next.playlistPosition >= contentIds.length
      ) {
        await removeFromQueue(next.id);
        return fetchNext();
      }

      // Resolve playlist to content
      const content = await resolvePlaylistContent(next);
      if (!content) {
        // Failed to resolve, remove and try next
        await removeFromQueue(next.id);
        return fetchNext();
      }

      setCurrentPlaylistState(next);
      setNowPlayingState(content);
      return content;
    }

    // Regular content
    setCurrentPlaylistState(null);
    setNowPlayingState(next);
    return next;
  }, [removeFromQueue, resolvePlaylistContent]);

  // Advance to next item in current playlist
  const advanceInPlaylist = useCallback(async (): Promise<ListData | null> => {
    if (!currentPlaylist) return null;

    const contentIds: string[] = JSON.parse(currentPlaylist.contentIds || '[]');
    const currentIndex = currentPlaylist.playlistPosition ?? 0;
    const nextIndex = currentIndex + 1;

    if (nextIndex >= contentIds.length) {
      // End of playlist
      return null;
    }

    // Update playlist position on server
    await fetch('/api/queue/add', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contentId: currentPlaylist.id,
        playlistPosition: nextIndex,
      }),
    });

    // Fetch and set the next content
    const content = await resolvePlaylistContent(currentPlaylist, nextIndex);
    if (!content) return null;

    // Update local state
    setCurrentPlaylistState({
      ...currentPlaylist,
      playlistPosition: nextIndex,
    });
    setNowPlayingState(content);

    return content;
  }, [currentPlaylist, resolvePlaylistContent]);

  // Go back to previous item in current playlist
  const retreatInPlaylist = useCallback(async (): Promise<ListData | null> => {
    if (!currentPlaylist) return null;

    const currentIndex = currentPlaylist.playlistPosition ?? 0;
    const prevIndex = currentIndex - 1;

    if (prevIndex < 0) {
      // Already at beginning
      return null;
    }

    // Update playlist position on server
    await fetch('/api/queue/add', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contentId: currentPlaylist.id,
        playlistPosition: prevIndex,
      }),
    });

    // Fetch and set the previous content
    const content = await resolvePlaylistContent(currentPlaylist, prevIndex);
    if (!content) return null;

    // Update local state
    setCurrentPlaylistState({
      ...currentPlaylist,
      playlistPosition: prevIndex,
    });
    setNowPlayingState(content);

    return content;
  }, [currentPlaylist, resolvePlaylistContent]);

  // Fetch previous content from history
  const fetchPreviousFromHistory = useCallback(
    async (excludeId: string | null): Promise<ListData | null> => {
      const response = await fetch('/api/history', {
        credentials: 'include',
      });
      const historyData = await response.json();
      const history = (historyData.items || []) as ListData[];

      const target = history.find(
        (item) => item.type === 'content' && item.id !== excludeId
      );

      if (!target) return null;

      // Reset position if finished
      const finished =
        (target.secondsTotal || 0) > 0 &&
        (target.secondsListened || 0) >= (target.secondsTotal || 0) - 1;

      return {
        ...target,
        secondsListened: finished ? 0 : target.secondsListened || 0,
      };
    },
    []
  );

  return {
    // State
    nowPlaying,
    nowPlayingId,
    currentPlaylist,

    // Methods
    setNowPlaying,
    setCurrentPlaylist,

    // Queue operations
    fetchNext,
    advanceInPlaylist,
    retreatInPlaylist,
    removeFromQueue,
    addToQueue,

    // Utility
    resolvePlaylistContent,
    fetchPreviousFromHistory,
  };
}
